import type { App, Component } from 'vue'
import { ZAlert } from '@zoryn/components/alert'
import { ZAutocomplete } from '@zoryn/components/autocomplete'
import { ZBadge } from '@zoryn/components/badge'
import { ZButton } from '@zoryn/components/button'
import { ZCard } from '@zoryn/components/card'
import { ZCascader } from '@zoryn/components/cascader'
import {
  ZCheckbox,
  ZCheckboxButton,
  ZCheckboxGroup,
} from '@zoryn/components/checkbox'
import { ZCol } from '@zoryn/components/col'
import { ZCollapse, ZCollapseItem } from '@zoryn/components/collapse'
import { ZColorPicker } from '@zoryn/components/color-picker'
import {
  ZAside,
  ZContainer,
  ZFooter,
  ZHeader,
  ZMain,
} from '@zoryn/components/container'
import { ZDatePicker } from '@zoryn/components/date-picker'
import { ZDialog } from '@zoryn/components/dialog'
import { ZDivider } from '@zoryn/components/divider'
import {
  ZDropdown,
  ZDropdownItem,
  ZDropdownMenu,
} from '@zoryn/components/dropdown'
import { ZForm, ZFormItem } from '@zoryn/components/form'
import { ZIcon } from '@zoryn/components/icon'
import { ZImage } from '@zoryn/components/image'
import { ZInput } from '@zoryn/components/input'
import { ZInputNumber } from '@zoryn/components/input-number'
import { ZInputOtp } from '@zoryn/components/input-otp'
import { ZInputTag } from '@zoryn/components/input-tag'
import { ZMention } from '@zoryn/components/mention'
import { ZMenu, ZMenuItem } from '@zoryn/components/menu'
import { ZPopconfirm } from '@zoryn/components/popconfirm'
import { ZPopover } from '@zoryn/components/popover'
import { ZRadio, ZRadioButton, ZRadioGroup } from '@zoryn/components/radio'
import { ZRate } from '@zoryn/components/rate'
import { ZRow } from '@zoryn/components/row'
import { ZOption, ZOptionGroup, ZSelect } from '@zoryn/components/select'
import { ZSlider } from '@zoryn/components/slider'
import { ZSpace } from '@zoryn/components/space'
import { ZSwitch } from '@zoryn/components/switch'
import { ZTabPane, ZTabs } from '@zoryn/components/tabs'
import { ZTag } from '@zoryn/components/tag'
import { ZText } from '@zoryn/components/text'
import { ZTimePicker } from '@zoryn/components/time-picker'
import { ZTimeSelect } from '@zoryn/components/time-select'
import { ZTooltip } from '@zoryn/components/tooltip'
import { ZTransfer } from '@zoryn/components/transfer'
import { ZTree } from '@zoryn/components/tree'
import { ZTreeSelect } from '@zoryn/components/tree-select'
import { ZUpload } from '@zoryn/components/upload'

export type FormCreateLike = {
  component: (name: string, component: Component) => unknown
}

const EL_COMPONENTS: Array<[string, Component]> = [
  ['ElAlert', ZAlert],
  ['ElAutocomplete', ZAutocomplete],
  ['ElBadge', ZBadge],
  ['ElButton', ZButton],
  ['ElCard', ZCard],
  ['ElCascader', ZCascader],
  ['ElCheckbox', ZCheckbox],
  ['ElCheckboxButton', ZCheckboxButton],
  ['ElCheckboxGroup', ZCheckboxGroup],
  ['ElCol', ZCol],
  ['ElCollapse', ZCollapse],
  ['ElCollapseItem', ZCollapseItem],
  ['ElColorPicker', ZColorPicker],
  ['ElContainer', ZContainer],
  ['ElAside', ZAside],
  ['ElHeader', ZHeader],
  ['ElMain', ZMain],
  ['ElFooter', ZFooter],
  ['ElDatePicker', ZDatePicker],
  ['ElDialog', ZDialog],
  ['ElDivider', ZDivider],
  ['ElDropdown', ZDropdown],
  ['ElDropdownItem', ZDropdownItem],
  ['ElDropdownMenu', ZDropdownMenu],
  ['ElForm', ZForm],
  ['ElFormItem', ZFormItem],
  ['ElIcon', ZIcon],
  ['ElImage', ZImage],
  ['ElInput', ZInput],
  ['ElInputNumber', ZInputNumber],
  ['ElInputOtp', ZInputOtp],
  ['ElInputTag', ZInputTag],
  ['ElMention', ZMention],
  ['ElMenu', ZMenu],
  ['ElMenuItem', ZMenuItem],
  ['ElPopconfirm', ZPopconfirm],
  ['ElPopover', ZPopover],
  ['ElRadio', ZRadio],
  ['ElRadioButton', ZRadioButton],
  ['ElRadioGroup', ZRadioGroup],
  ['ElRate', ZRate],
  ['ElRow', ZRow],
  ['ElSelect', ZSelect],
  ['ElOption', ZOption],
  ['ElOptionGroup', ZOptionGroup],
  ['ElSlider', ZSlider],
  ['ElSpace', ZSpace],
  ['ElSwitch', ZSwitch],
  ['ElTabs', ZTabs],
  ['ElTabPane', ZTabPane],
  ['ElTag', ZTag],
  ['ElText', ZText],
  ['ElTimePicker', ZTimePicker],
  ['ElTimeSelect', ZTimeSelect],
  ['ElTooltip', ZTooltip],
  ['ElTransfer', ZTransfer],
  ['ElTree', ZTree],
  ['ElTreeSelect', ZTreeSelect],
  ['ElUpload', ZUpload],
]

const TYPE_ALIASES: Array<[string, Component]> = [
  ['elAlert', ZAlert],
  ['elButton', ZButton],
  ['elCard', ZCard],
  ['elCollapse', ZCollapse],
  ['elCollapseItem', ZCollapseItem],
  ['elDivider', ZDivider],
  ['elImage', ZImage],
  ['elTag', ZTag],
  ['elTabs', ZTabs],
  ['elTabPane', ZTabPane],
  ['elTransfer', ZTransfer],
  ['elTreeSelect', ZTreeSelect],
  ['date-picker', ZDatePicker],
  ['input-number', ZInputNumber],
  ['color-picker', ZColorPicker],
  ['time-picker', ZTimePicker],
  ['time-select', ZTimeSelect],
  ['tree-select', ZTreeSelect],
  ['input-tag', ZInputTag],
  ['input-otp', ZInputOtp],
  ['space', ZSpace],
]

const boundApps = new WeakSet<App>()
const boundEngines = new WeakSet<object>()

export function bindZorynElAliases(app: App) {
  if (boundApps.has(app)) return
  boundApps.add(app)
  for (const [name, component] of EL_COMPONENTS) {
    if (!app.component(name)) {
      app.component(name, component)
    }
  }
}

export function bindZorynFormCreate(fc: FormCreateLike) {
  if (boundEngines.has(fc)) return
  boundEngines.add(fc)
  for (const [name, component] of EL_COMPONENTS) {
    fc.component(name, component)
  }
  for (const [name, component] of TYPE_ALIASES) {
    fc.component(name, component)
  }
}

/** @deprecated Use bindZorynFormCreate. Shell mapping is safe after designer CSS uses z-*. */
export function bindDesignerFormCreate(fc: FormCreateLike) {
  bindZorynFormCreate(fc)
}
